/**
 * Error logging service
 * Replaces console.error with a proper logging mechanism
 * In production, this would send errors to a service like Sentry
 */

export enum LogLevel {
  DEBUG = 'debug',
  INFO = 'info',
  WARN = 'warn',
  ERROR = 'error',
}

interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp: string;
  context?: Record<string, unknown>;
  error?: Error;
}

// In-memory log storage for development
const logBuffer: LogEntry[] = [];
const MAX_BUFFER_SIZE = 100;

/**
 * Adds a log entry to the buffer
 */
function addToBuffer(entry: LogEntry): void {
  logBuffer.push(entry);
  if (logBuffer.length > MAX_BUFFER_SIZE) {
    logBuffer.shift();
  }
}

/**
 * Logs a message with the specified level
 */
export function log(
  level: LogLevel,
  message: string,
  context?: Record<string, unknown>,
  error?: Error
): void {
  const entry: LogEntry = {
    level,
    message,
    timestamp: new Date().toISOString(),
    context,
    error,
  };

  addToBuffer(entry);

  // In development, still log to console
  if (import.meta.env.DEV) {
    const consoleMethod = level === LogLevel.ERROR ? 'error' : 
                         level === LogLevel.WARN ? 'warn' : 
                         level === LogLevel.INFO ? 'info' : 'log';
    
    if (error) {
      console[consoleMethod](`[${level.toUpperCase()}] ${message}`, error, context);
    } else {
      console[consoleMethod](`[${level.toUpperCase()}] ${message}`, context);
    }
  }

  // TODO: In production, send to error tracking service
  // if (import.meta.env.PROD && level === LogLevel.ERROR) {
  //   sendToErrorTrackingService(entry);
  // }
}

/**
 * Logs an error with context
 */
export function logError(
  message: string,
  error: Error,
  context?: Record<string, unknown>
): void {
  log(LogLevel.ERROR, message, context, error);
}

/**
 * Logs a warning
 */
export function logWarn(message: string, context?: Record<string, unknown>): void {
  log(LogLevel.WARN, message, context);
}

/**
 * Logs info
 */
export function logInfo(message: string, context?: Record<string, unknown>): void {
  log(LogLevel.INFO, message, context);
}

/**
 * Gets recent logs (for debugging)
 */
export function getRecentLogs(): LogEntry[] {
  return [...logBuffer];
}

/**
 * Clears the log buffer
 */
export function clearLogs(): void {
  logBuffer.length = 0;
}
